import React from 'react'
import { AlertTriangle, RefreshCw, Mail, ShieldAlert, HelpCircle } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { useApprovalQueue } from '../hooks/useApprovalQueue'
import ApprovalQueueSection from './ApprovalQueueSection'

type Props = {
  workspaceId: string
}

// The only two action types that currently reach `approval` mode (see
// automation-architecture.md's Approval Queue section) — each gets its own
// section here instead of one blended list, so "what is this for" is answered
// by the section header rather than a per-item badge.
const REENGAGEMENT_KEY = 'automation.reengagement_email'
const OBJECTION_KEY = 'automation.objection_reply'

const ApprovalsSettingsSection: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const { items, loading, error, retry, resolvingIds, resolveErrors, approve, dismiss } = useApprovalQueue(workspaceId)

  const reengagementItems = items.filter((i) => i.actionKey === REENGAGEMENT_KEY)
  const objectionItems = items.filter((i) => i.actionKey === OBJECTION_KEY)
  // Defensive catch-all — if a third action type ever reaches `approval` mode
  // before this page is updated for it, its items still show up here instead
  // of silently vanishing from view.
  const otherItems = items.filter((i) => i.actionKey !== REENGAGEMENT_KEY && i.actionKey !== OBJECTION_KEY)

  if (loading) {
    return <div className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: t.hoverBackground }} />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl" style={{ backgroundColor: t.errorBackground }}>
          <AlertTriangle size={24} style={{ color: t.errorText }} />
        </div>
        <div>
          <p className="font-semibold text-base" style={{ color: t.text }}>Couldn&apos;t load the approval queue</p>
          <p className="text-sm mt-1" style={{ color: t.mutedText }}>{error}</p>
        </div>
        <button
          type="button"
          onClick={retry}
          className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-[12.5px]" style={{ color: t.mutedText }}>
        AI-drafted emails waiting for a decision. Anyone in the workspace can approve, edit, or dismiss these.
      </p>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Mail size={14} style={{ color: t.mutedText }} />
          <label className="text-sm font-semibold" style={{ color: t.text }}>Re-engagement follow-ups</label>
          {reengagementItems.length > 0 && (
            <span
              className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold"
              style={{ backgroundColor: t.accent, color: t.buttonText }}
            >
              {reengagementItems.length}
            </span>
          )}
        </div>
        <ApprovalQueueSection
          items={reengagementItems}
          resolvingIds={resolvingIds}
          resolveErrors={resolveErrors}
          approve={approve}
          dismiss={dismiss}
          emptyMessage="No re-engagement drafts waiting."
        />
      </div>

      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} style={{ color: t.mutedText }} />
          <label className="text-sm font-semibold" style={{ color: t.text }}>Objection replies</label>
          {objectionItems.length > 0 && (
            <span
              className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold"
              style={{ backgroundColor: t.accent, color: t.buttonText }}
            >
              {objectionItems.length}
            </span>
          )}
        </div>
        <ApprovalQueueSection
          items={objectionItems}
          resolvingIds={resolvingIds}
          resolveErrors={resolveErrors}
          approve={approve}
          dismiss={dismiss}
          emptyMessage="No objection replies waiting."
        />
      </div>

      {otherItems.length > 0 && (
        <div className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <HelpCircle size={14} style={{ color: t.mutedText }} />
            <label className="text-sm font-semibold" style={{ color: t.text }}>Other</label>
            <span
              className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10.5px] font-semibold"
              style={{ backgroundColor: t.accent, color: t.buttonText }}
            >
              {otherItems.length}
            </span>
          </div>
          <ApprovalQueueSection
            items={otherItems}
            resolvingIds={resolvingIds}
            resolveErrors={resolveErrors}
            approve={approve}
            dismiss={dismiss}
            emptyMessage=""
          />
        </div>
      )}
    </div>
  )
}

export default React.memo(ApprovalsSettingsSection)
